/* eslint-disable */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useFormik } from "formik";
import confirmModal from "../../components/custom_antd/confirmModal";
import { PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useTranslation } from "react-i18next"; // Added import for translations
import uploadIcon from "../../assets/icons/upload.svg";
import apiClient from "../../helpers/apiClient";
import ImageCompressor from "../../utilities/ImageCompressor";
import TopBack from "../reservations/components/subcompdash/Topback";
import CategoryFormModal from "./components/category/CategoryFormModal";
import AllCategories from "./components/category/AllCategories";
import { useToast } from "../../contexts/ToastContext";
import uploadFile from "../../utilities/uploadFile";
import useCarPricingMode from "./hooks/useCarPricingMode";
import useOpenModalParam from "../../hooks/useOpenModalParam";
import CategoryDetails from "./components/category/CategoryDetails";
const CATEGORY_VIEWS = {
    LIST: "list",
    DETAILS: "details",
};

export default function Rates({ embedded = false }) {
    const { t } = useTranslation("cars"); // Added translation hook
    const isCarPricingMode = useCarPricingMode();
    const [categoryView, setCategoryView] = useState(CATEGORY_VIEWS.LIST);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [totalCategories, setTotalCategories] = useState(0);

    const goToCategoryList = () => {
        setCategoryView(CATEGORY_VIEWS.LIST);
        setSelectedCategoryId(null);
    };

    const openCreateModal = () => {
        setEditingCategoryId(null);
        setCategoryModalOpen(true);
    };

    const openEditModal = (categoryId) => {
        setEditingCategoryId(categoryId);
        setCategoryModalOpen(true);
    };

    const closeCategoryModal = () => {
        setCategoryModalOpen(false);
        setEditingCategoryId(null);
    };

    useOpenModalParam("addCategory", openCreateModal);

    const categoryModal = (
        <CategoryFormModal
            open={categoryModalOpen}
            categoryId={editingCategoryId}
            onCancel={closeCategoryModal}
            onSuccess={closeCategoryModal}
        />
    );

    if (categoryView === CATEGORY_VIEWS.DETAILS) {
        const detailsContent = (
            <>
                <CategoryDetails
                    embedded
                    categoryId={selectedCategoryId}
                    onCancel={goToCategoryList}
                    onEdit={() => openEditModal(selectedCategoryId)}
                    onDeleted={goToCategoryList}
                />
                {categoryModal}
            </>
        );

        if (embedded) {
            return detailsContent;
        }

        return (
            <div>
                {detailsContent}
            </div>
        );
    }

    const pageContent = (
        <div>
            {isCarPricingMode && <div className="mb-3 text-sm text-gray-500">{t("rates.labelModeNote")}</div>}
            <AllCategories
                title={t("rates.allCarCategories")}
                headerExtra={
                    <Button icon={<PlusOutlined />} onClick={openCreateModal}>
                        {t("rates.addCategory")}
                    </Button>
                }
                setTotalCategories={setTotalCategories}
                onEditCategory={openEditModal}
                onViewDetails={(categoryId) => {
                    setSelectedCategoryId(categoryId);
                    setCategoryView(CATEGORY_VIEWS.DETAILS);
                }}
            />
        </div>
    );

    const pageWithModal = (
        <>
            {pageContent}
            {categoryModal}
        </>
    );

    if (embedded) {
        return pageWithModal;
    }

    return (
        <>
            <TopBack />
            <div>
                {pageWithModal}
            </div>
        </>
    );
}

Rates.propTypes = {
    embedded: PropTypes.bool,
};

function CatCard(props) {
    const { t } = useTranslation("cars"); // Added translation hook
    const toast = useToast();
    const { catDetails, reload } = props;
    const [editMode, setEditMode] = useState(false);
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const cardForm = useFormik({
        initialValues: {
            name: catDetails.name,
            prevType: catDetails.prevType,
            hourly: catDetails.hourly,
            daily: catDetails.daily,
            weekly: catDetails.weekly,
            seat: catDetails.seat,
            bags: catDetails.bags,
            image: null,
        },
        onSubmit: async (values) => {
            if (!values.name || !values.seat || !values.bags || !values.hourly || !values.weekly || !values.daily) {
                toast.error({
                    message: t("rates.allFieldsRequired"),
                });
                return;
            }

            try {
                setEditing(true);

                const updatedValues = {
                    ...values,
                    prevType: values.name.toLowerCase(),
                    name: values.name.toLowerCase(),
                };

                if (values.image) {
                    updatedValues.image = await uploadFile(values.image);
                }
                if (!updatedValues.image) {
                    updatedValues.image = catDetails?.image ?? "";
                }

                const response = await apiClient.patch(`/api/car-category/${catDetails.id}`, updatedValues);

                if (response?.status === 200) {
                    toast.success({ message: t("rates.categoryEdited") });

                    cardForm.resetForm();
                    reload();
                    setEditMode(false);
                    setEditing(false);
                }
            } catch (err) {
                let errorMsg = t("rates.errorSavingUpdates");
                if (err.response?.status === 401) {
                    errorMsg = t("rates.notAuthorized");
                }
                toast.error({ message: errorMsg });
                console.error("err", err);
                setEditMode(false);
                setEditing(false);
            }
        },
    });

    useEffect(() => {
        cardForm.setValues({
            name: catDetails.name,
            prevType: catDetails.prevType,
            hourly: catDetails.hourly,
            daily: catDetails.daily,
            weekly: catDetails.weekly,
            seat: catDetails.seat,
            bags: catDetails.bags,
            image: null,
        });
    }, [catDetails]);

    const handleDeleteClick = async () => {
        const confirmed = await confirmModal({
            title: t("rates.confirmDelete"),
            okText: t("delete"),
            cancelText: t("cancel"),
            danger: true,
        });

        if (confirmed) {
            try {
                setDeleting(true);
                const response = await apiClient.delete(`/api/car-category/${catDetails.id}`);

                if (response?.status === 201) {
                    toast.success({ message: t("rates.categoryDeleted") });
                    setDeleting(false);
                    cardForm.resetForm();
                    reload();
                }
            } catch (error) {
                let errorMsg = t("rates.errorSavingUpdates");
                if (error.response?.status === 401) {
                    errorMsg = t("rates.notAuthorized");
                }
                toast.error({ message: errorMsg });
                console.log(error);
                setDeleting(false);
            }
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            cardForm.setFieldValue("image", file);
        }
    };

    const handleDivClick = () => {
        document.getElementById("image").click();
    };

    return (
        <div
            style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "1rem",
            }}>
            <div style={{ display: "flex", justifyContent: "end", fontSize: "14px" }}>
                {!deleting && !editing ? (
                    <div
                        className="hoverBlue"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            if (editMode) {
                                cardForm.handleSubmit();
                            }
                            setEditMode((prev) => !prev);
                        }}>
                        {editMode ? t("rates.confirmEdit") : t("edit")}
                    </div>
                ) : null}

                <div
                    className="hoverRed"
                    style={{ cursor: "pointer", marginLeft: "1rem" }}
                    onClick={() => {
                        if (editMode) {
                            setEditMode((prev) => !prev);
                            cardForm.resetForm();
                        } else {
                            handleDeleteClick();
                        }
                    }}>
                    {editing ? t("rates.savingChanges") : ""}
                    {deleting ? t("rates.deleting") : ""}
                    {!editing && !deleting && editMode ? t("rates.cancel") : ""}
                    {!editing && !deleting && !editMode ? t("delete") : ""}
                </div>
            </div>
            {editMode ? (
                <>
                    <div
                        className="editCustom"
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                        }}>
                        <div style={{ flex: "1" }}>
                            <div>
                                <input
                                    name="name"
                                    value={cardForm.values.name}
                                    onChange={cardForm.handleChange}
                                    type="text"
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: "22px",
                                        width: "10rem",
                                    }}
                                />
                            </div>
                            <div>
                                <input
                                    name="seat"
                                    value={cardForm.values.seat}
                                    onChange={cardForm.handleChange}
                                    type="number"
                                    style={{
                                        width: "3rem",
                                    }}
                                />{" "}
                                {t("seats")}
                            </div>
                            <div>
                                <input
                                    name="bags"
                                    value={cardForm.values.bags}
                                    onChange={cardForm.handleChange}
                                    type="number"
                                    style={{
                                        width: "3rem",
                                    }}
                                />{" "}
                                {t("rates.bags")}
                            </div>
                        </div>
                        <div
                            style={{
                                height: "134px",
                                width: "150px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f0f2f0",
                                margin: "10px 0px",
                                borderRadius: "10px",
                                cursor: "pointer",
                            }}
                            onClick={handleDivClick}>
                            <h3
                                style={{
                                    fontSize: "14px",
                                    padding: "0px",
                                    marginBottom: "4px",
                                }}>
                                {t("rates.changeImage")}
                            </h3>
                            <img
                                src={
                                    cardForm.values.image
                                        ? URL.createObjectURL(cardForm.values.image)
                                        : catDetails?.image
                                }
                                alt={t("rates.uploadIcon")}
                                style={{ width: "50px", objectFit: "cover" }}
                            />
                            <input
                                id="image"
                                name="image"
                                type="file"
                                style={{ display: "none" }}
                                onChange={(event) => {
                                    handleFileChange(event);
                                }}
                                accept="image/*"
                            />
                        </div>
                    </div>
                    <div
                        className="editCustom"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "auto auto auto",
                            gap: "1rem",
                        }}>
                        <div
                            style={{
                                padding: "1.5rem",
                                backgroundColor: "#f2f2fa",
                                borderRadius: "1rem",
                                textAlign: "center",
                            }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                                $
                                <input
                                    name="hourly"
                                    value={cardForm.values.hourly}
                                    onChange={cardForm.handleChange}
                                    type="number"
                                    style={{
                                        width: "4rem",
                                        fontWeight: "bold",
                                        fontSize: "18px",
                                    }}
                                />
                            </span>
                            /{t("hour")}
                        </div>

                        <div
                            style={{
                                padding: "1.5rem",
                                backgroundColor: "#f2f2fa",
                                borderRadius: "1rem",
                                textAlign: "center",
                            }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                                $
                                <input
                                    name="daily"
                                    value={cardForm.values.daily}
                                    onChange={cardForm.handleChange}
                                    type="number"
                                    style={{
                                        width: "4rem",
                                        fontWeight: "bold",
                                        fontSize: "18px",
                                    }}
                                />
                            </span>
                            /{t("day")}
                        </div>

                        <div
                            style={{
                                padding: "1.5rem",
                                backgroundColor: "#f2f2fa",
                                borderRadius: "1rem",
                                textAlign: "center",
                            }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                                $
                                <input
                                    name="weekly"
                                    value={cardForm.values.weekly}
                                    onChange={cardForm.handleChange}
                                    type="number"
                                    style={{
                                        width: "4rem",
                                        fontWeight: "bold",
                                        fontSize: "18px",
                                    }}
                                />
                            </span>
                            /{t("week")}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                        }}>
                        <div style={{ flex: "1" }}>
                            <div
                                style={{
                                    fontWeight: "bold",
                                    fontSize: "22px",
                                    textTransform: "uppercase",
                                }}>
                                {catDetails?.name}
                            </div>
                            <div>
                                {catDetails?.seat || "-"} {t("seats")}
                            </div>
                            <div>
                                {catDetails?.bags || "-"} {t("rates.bags")}
                            </div>
                        </div>
                        <div>
                            <img
                                src={catDetails?.image}
                                alt={t("car")}
                                style={{ height: "150px", width: "200px", objectFit: "cover" }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "auto auto auto",
                            gap: "1rem",
                        }}>
                        <div
                            style={{
                                padding: "1.5rem",
                                backgroundColor: "#f2f2fa",
                                borderRadius: "1rem",
                                textAlign: "center",
                            }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>${catDetails?.hourly}</span>/
                            {t("hour")}
                        </div>

                        <div
                            style={{
                                padding: "1.5rem",
                                backgroundColor: "#f2f2fa",
                                borderRadius: "1rem",
                                textAlign: "center",
                            }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>${catDetails?.daily}</span>/
                            {t("day")}
                        </div>

                        <div
                            style={{
                                padding: "1.5rem",
                                backgroundColor: "#f2f2fa",
                                borderRadius: "1rem",
                                textAlign: "center",
                            }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>${catDetails?.weekly}</span>/
                            {t("week")}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

CatCard.propTypes = {
    catDetails: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        prevType: PropTypes.string,
        hourly: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        daily: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        weekly: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        seat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bags: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        image: PropTypes.string,
        ind: PropTypes.number,
    }).isRequired,
    reload: PropTypes.func.isRequired,
};

function AddCard({ reload }) {
    const { t } = useTranslation("cars"); // Added translation hook
    const toast = useToast();
    const [isAdding, setIsAdding] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const addForm = useFormik({
        initialValues: {
            name: "",
            seat: "",
            bags: "",
            hourly: "",
            daily: "",
            weekly: "",
            image: null,
        },
        onSubmit: async (values) => {
            if (isUploading) return;

            if (!values.name || !values.seat || !values.bags || !values.hourly || !values.weekly || !values.daily) {
                toast.error({ message: t("rates.allFieldsRequired") });
                return;
            }

            if (!values.image) {
                toast.error({ message: t("rates.imageRequired") });
                return;
            }

            try {
                const updatedValues = {
                    ...values,
                    prevType: values.name.toLowerCase(),
                    name: values.name.toLowerCase(),
                };
                setIsUploading(true);

                const imageData = new FormData();
                let img = "";
                const compressedImage = await ImageCompressor(values.image);
                imageData.append("image", compressedImage);
                imageData.append("imagePath", "Cars");

                const resp = await axios.post(
                    `${window.REACT_APP_SOFTCAR_OLD_API || "/api"}/uploadSingleImage`,
                    imageData,
                );

                if (resp?.data?.success) {
                    img = resp?.data.url || "";
                }

                const res = await apiClient.post("/api/car-category/create", {
                    ...updatedValues,
                    prevType: updatedValues.name,
                    image: img,
                });

                if (res.data.success) {
                    setIsUploading(false);
                    setIsAdding(false);
                    addForm.resetForm();
                    reload();
                } else {
                    setIsUploading(false);
                    console.error("Error adding category: ", res.data.message);
                    toast.error({ message: t("rates.errorUploading") });
                }
            } catch (error) {
                let errorMsg = t("rates.errorSavingUpdates");
                if (error.response?.status === 401) {
                    errorMsg = t("rates.notAuthorized");
                }
                toast.error({ message: errorMsg });
                console.error("err", error);
                setIsUploading(false);
            }
        },
    });

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            addForm.setFieldValue("image", file);
        }
    };

    const handleDivClick = () => {
        document.getElementById("image").click();
    };

    if (!isAdding) {
        return (
            <div
                style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "1rem",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                }}
                onClick={() => setIsAdding(true)}>
                <div style={{ fontSize: "150px", color: "gray" }}>+</div>
            </div>
        );
    }

    return (
        <div
            style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "1rem",
            }}>
            <div style={{ display: "flex", justifyContent: "end", fontSize: "14px" }}>
                <div className="hoverBlue" style={{ cursor: "pointer" }} onClick={addForm.handleSubmit}>
                    {isUploading ? t("rates.saving") : t("add")}
                </div>
                <div
                    className="hoverRed"
                    style={{ cursor: "pointer", marginLeft: "1rem" }}
                    onClick={() => {
                        setIsAdding(false);
                        addForm.resetForm();
                    }}>
                    {t("rates.cancel")}
                </div>
            </div>
            <div
                className="editCustom"
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                <div style={{ flex: "1" }}>
                    <div>
                        <input
                            name="name"
                            value={addForm.values.name}
                            onChange={addForm.handleChange}
                            type="text"
                            placeholder={t("rates.categoryNamePlaceholder")}
                            style={{
                                fontWeight: "bold",
                                fontSize: "22px",
                                width: "10rem",
                            }}
                        />
                    </div>
                    <div>
                        <input
                            name="seat"
                            value={addForm.values.seat}
                            onChange={addForm.handleChange}
                            type="number"
                            placeholder={t("rates.seatsPlaceholder")}
                            style={{
                                width: "3rem",
                            }}
                        />{" "}
                        {t("seats")}
                    </div>
                    <div>
                        <input
                            name="bags"
                            value={addForm.values.bags}
                            onChange={addForm.handleChange}
                            type="number"
                            placeholder={t("rates.bagsPlaceholder")}
                            style={{
                                width: "3rem",
                            }}
                        />{" "}
                        {t("rates.bags")}
                    </div>
                </div>
                <div
                    style={{
                        height: "134px",
                        width: "150px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f2f0",
                        margin: "10px 0px",
                        borderRadius: "10px",
                        cursor: "pointer",
                    }}
                    onClick={handleDivClick}>
                    <h3 style={{ fontSize: "14px", padding: "0px", marginBottom: "4px" }}>
                        {addForm.values.image ? t("rates.changeImage") : t("rates.uploadImage")}
                    </h3>
                    <img
                        src={addForm.values?.image ? URL.createObjectURL(addForm.values?.image) : uploadIcon}
                        alt={t("rates.uploadIcon")}
                        style={{ width: "50px", objectFit: "cover" }}
                    />
                    <input
                        id="image"
                        name="image"
                        type="file"
                        style={{ display: "none" }}
                        onChange={(event) => {
                            handleFileChange(event);
                        }}
                        accept="image/*"
                    />
                </div>
            </div>
            <div
                className="editCustom"
                style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto auto",
                    gap: "1rem",
                }}>
                <div
                    style={{
                        padding: "1.5rem",
                        backgroundColor: "#f2f2fa",
                        borderRadius: "1rem",
                        textAlign: "center",
                    }}>
                    <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                        $
                        <input
                            name="hourly"
                            value={addForm.values.hourly}
                            onChange={addForm.handleChange}
                            type="number"
                            placeholder={t("rates.hourlyRatePlaceholder")}
                            style={{
                                width: "4rem",
                                fontWeight: "bold",
                                fontSize: "18px",
                            }}
                        />
                    </span>
                    /{t("hour")}
                </div>

                <div
                    style={{
                        padding: "1.5rem",
                        backgroundColor: "#f2f2fa",
                        borderRadius: "1rem",
                        textAlign: "center",
                    }}>
                    <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                        $
                        <input
                            name="daily"
                            value={addForm.values.daily}
                            onChange={addForm.handleChange}
                            type="number"
                            placeholder={t("rates.dailyRatePlaceholder")}
                            style={{
                                width: "4rem",
                                fontWeight: "bold",
                                fontSize: "18px",
                            }}
                        />
                    </span>
                    /{t("day")}
                </div>

                <div
                    style={{
                        padding: "1.5rem",
                        backgroundColor: "#f2f2fa",
                        borderRadius: "1rem",
                        textAlign: "center",
                    }}>
                    <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                        $
                        <input
                            name="weekly"
                            value={addForm.values.weekly}
                            onChange={addForm.handleChange}
                            type="number"
                            placeholder={t("rates.weeklyRatePlaceholder")}
                            style={{
                                width: "4rem",
                                fontWeight: "bold",
                                fontSize: "18px",
                            }}
                        />
                    </span>
                    /{t("week")}
                </div>
            </div>
        </div>
    );
}

AddCard.propTypes = {
    reload: PropTypes.func.isRequired,
};
